import moment from 'moment-timezone';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';

// Note: not translatable
export const formatYYYYMMDD: string = 'YYYY-MM-DD';

// Load the UTC plugin
dayjs.extend(utcPlugin);

// Formats dates and times. Provides named enums for formatting options.
// You can set and/or show the timezone used as well.

// TODO: Import this test file form smart mouthguard.
// See the test file for many examples (datetime.test.ts)

// If you want to try different timezones see a timezone list at:
// https://en.wikipedia.org/wiki/List_of_tz_database_time_zones

// If you want to add new formats:
// Either read the moment docs: https://momentjs.com/docs/#/displaying/format/
// or this cheat-sheet:  https://m.html.cn/dev/moment.html
export enum DateTimeFormats {
  DayAndPartOfDay = '_DAY_NAME_AND_SECTION_',
  DoctoBookingSystemDayMonthYear = 'DD-MM-YYYY',
}

export enum TimeZoneFormat {
  None = '',
  Name = 'Name',
  Abbreviation = 'Abbreviation',
  Offset = 'Offset',
}

class DateTime {
  defaultTimezone: string = '';
  // Which timezone to use where none specified. Set to the device's current timezone initially.
  // defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // constructor(defaultTimezone?: string) {
  //   if (defaultTimezone) this.defaultTimezone = defaultTimezone;
  // }

  constructor() {
    this.useDeviceTimezone();
  }

  useDeviceTimezone() {
    // Reset the default timezone to device's current timezone if you've changed it
    this.defaultTimezone = this.getDeviceTimezone();
  }

  getDeviceTimezone() {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Converts a date string in 'DD/MM/YYYY' or 'MM/DD/YYYY' format to a UTC ISO date string ('YYYY-MM-DD').
   *
   * @param {string} inputDate - The input date string.
   * @param {boolean} isDMYFormat - Whether the input date is in 'DD/MM/YYYY' format (true) or 'MM/DD/YYYY' format (false).
   * @returns {string | null} The formatted UTC ISO date string ('YYYY-MM-DD') or null if input is invalid.
   */
  convertToDate(
    inputDate: string | undefined,
    isDMYFormat: boolean,
  ): string | null {
    if (!inputDate) {
      return null;
    }

    const parts = inputDate.split('/');

    if (parts.length === 3) {
      const [part1, part2, part3] = isDMYFormat
        ? parts
        : [parts[1], parts[0], parts[2]];
      const [day, month, year] = [part1, part2, part3].map(Number);

      // Check for (approximate) valid day, month, and year ranges
      const validDay = day >= 1 && day <= 31;
      const validMonth = month >= 1 && month <= 12;
      const validYear = year >= 1000 && year <= 9999;

      // Validate that the parts represent valid numbers - 1 or 2 for day/month, or 4 digits for year
      if (
        validDay &&
        validMonth &&
        validYear &&
        /^\d{1,2}$/.test(day.toString()) &&
        /^\d{1,2}$/.test(month.toString()) &&
        /^\d{4}$/.test(year.toString())
      ) {
        // Add leading zeros if necessary
        return `${year}-${month.toString().padStart(2, '0')}-${day
          .toString()
          .padStart(2, '0')}`;
      }
    }

    // Invalid input, return null
    return null;
  }

  /**
   * Checks if the input date string matches the specified format after formatting.
   *
   * @param {string | undefined} inputDate - The input date string.
   * @param {string} format - The desired date format to compare against.
   * @returns {boolean} True if the input date matches the format after formatting, false otherwise.
   */
  isFormattedAs(inputDate: string | undefined, format: string): boolean {
    const formattedDate: string = this.format(inputDate?.trim(), format);
    return formattedDate === inputDate;
  }

  /**
   * Formats the input date string as a UTC ISO date if possible.
   *
   * Usage of dayjs.utc(inputDate) will ensure that timezone offsets are ignored and
   * the date will not be altered to reflect the user's locale settings,
   * thus, this function can be used to safely format dates of birth.
   *
   * e.g. '2012-02-26T16:38:20.123Z' >> '2012-02-26'
   *
   * @param {string | undefined} inputDate - The input date string.
   * @returns {string} The formatted UTC ISO date string or an empty string.
   */
  formatToUtcIsoDate(inputDate: string | undefined): string {
    if (inputDate) {
      return dayjs.utc(inputDate).format(formatYYYYMMDD).toString();
    }
    return '';
  }

  /**
   * Formats the input date string as UTC midnight ISO datetime if possible.
   * If the input date is already in the desired format, returns it in UTC midnight format.
   * If the input date is not in the desired format, returns an empty string.
   *
   * Avoids using Date().toISOString() to ensure that no adjustment for timezone is made, thus,
   * this function can be used to safely format dates of birth.
   * e.g. '2012-02-26' >> '2012-02-26T00:00:00.000Z'
   *
   * @param {string | undefined} inputDate - The input date string.
   * @returns {string} The UTC midnight ISO datetime formatted string or an empty string.
   */
  formatToUTCMidnightIsoDateTime(inputDate: string | undefined): string {
    const formattedDate: string = dayjs(inputDate).format(formatYYYYMMDD);

    if (formattedDate === inputDate) {
      return `${inputDate}T00:00:00.000Z`;
    }
    return '';
  }

  format(
    date: Date | moment.Moment | string | number | undefined,
    formatType: DateTimeFormats | string,
    showTimezone: TimeZoneFormat = TimeZoneFormat.None,
    overrideTimezone: string = '',
  ) {
    if (date === undefined) return '';

    const momentDate = moment(date);
    const timezone = overrideTimezone || this.defaultTimezone;

    momentDate.tz(timezone);

    let formattedTime;
    if (formatType.startsWith('_'))
      // Special formats are handled by a custom function and start with "_"
      formattedTime = this.specialFormats[formatType.toString()](
        momentDate,
        showTimezone,
      );
    else formattedTime = momentDate.format(formatType);

    if (showTimezone !== TimeZoneFormat.None)
      formattedTime += ` ${this.displayTimeZone(
        momentDate,
        timezone,
        showTimezone,
      )}`;
    return formattedTime;
  }

  displayTimeZone(
    momentObject: moment.Moment,
    timezone: string,
    formatType: TimeZoneFormat,
  ) {
    if (formatType === TimeZoneFormat.Name) return timezone;
    if (formatType === TimeZoneFormat.Abbreviation)
      return momentObject.format('z');
    if (formatType === TimeZoneFormat.Offset) return momentObject.format('ZZ');

    return '';
  }

  specialFormats = {
    [DateTimeFormats.DayAndPartOfDay.toString()]: (
      momentDate: moment.Moment,
      showTimezone: TimeZoneFormat,
    ) => {
      return 'NEED TO IMPLEMENT: Add morning, afternoon, and evening to translator';
      // const localHour = momentDate.hour();
      // const day = momentDate.format('dddd');
      // let partOfDay;
      // if (localHour < 12)
      //   partOfDay = translator.translateText('generic:morning');
      // else if (localHour < 17)
      //   partOfDay = translator.translateText('generic:afternoon');
      // else if (localHour < 19)
      //   partOfDay = translator.translateText('generic:evening');
      // else partOfDay = 'Night';
      // return `${day} ${partOfDay}`;
    },
  };
}

export const calculateDateTimeBeforeEndDate = (
  endDateTime: any,
  numberOfDays: number,
) => endDateTime - 1000 * 60 * 60 * 24 * numberOfDays;

export const dateTime = new DateTime();

export const DateTimeClass = DateTime;
